// Send, Copy link and PDF on the office's invoice (QA-341). The link is the server's (invoices.send
// and invoices.copyLink build it with the one customer-link builder the portal uses); the office
// never puts a URL together itself.

import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { useMirror } from '../shared/mirror.ts';
import { invoiceShare, sentText, type InvoiceSummary } from './review-model.ts';
import './invoice-actions.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function InvoiceActions({
  invoice,
  onSent,
  sendWord = 'Send to customer',
}: {
  invoice: Pick<InvoiceSummary, 'id' | 'number' | 'status'>;
  onSent: () => void;
  /** "Send again" where it was sent before (the invoice sheet). */
  sendWord?: string;
}) {
  const can = invoiceShare(invoice.status);
  const mirroring = useMirror();
  const [busy, setBusy] = useState<'send' | 'link' | 'pdf' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);

  const run = async (what: 'send' | 'link' | 'pdf', fn: () => Promise<void>) => {
    setBusy(what);
    setError(null);
    setNote(null);
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  };

  const send = () =>
    run('send', async () => {
      const r = await client.call<{ sent: { channel: 'sms' | 'email' }[] }>('invoices.send', {
        invoiceId: invoice.id,
      });
      setNote(sentText(invoice.number, r.sent[0]?.channel ?? 'sms'));
      onSent();
    });

  const copy = () =>
    run('link', async () => {
      const r = await client.call<{ url: string; expiry: string }>('invoices.copyLink', {
        invoiceId: invoice.id,
      });
      setLink(r.url);
      // Where the browser won't write to the clipboard, the link stays on screen to copy by hand.
      const copied = await navigator.clipboard?.writeText(r.url).then(
        () => true,
        () => false,
      );
      setNote(copied ? `Link copied. It ${r.expiry}.` : `Copy the link below. It ${r.expiry}.`);
    });

  const pdf = () =>
    run('pdf', async () => {
      const r = await client.call<{ filename: string; base64: string }>('documents.invoicePdf', {
        invoiceId: invoice.id,
      });
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${r.base64}`;
      a.download = r.filename;
      a.click();
    });

  return (
    <div className="invoice-actions">
      <div className="row">
        {can.share && (
          <>
            {/* while the company mirrors ServiceTitan, its invoices go to the customer from there;
                the link's page already takes no payment then (QA-606) */}
            {!mirroring && (
              <button className="btn primary" disabled={!!busy} onClick={() => void send()}>
                {busy === 'send' ? 'Sending…' : sendWord}
              </button>
            )}
            <button className="btn" disabled={!!busy} onClick={() => void copy()}>
              {busy === 'link' ? 'Making the link…' : 'Copy link'}
            </button>
          </>
        )}
        <button className="btn" disabled={!!busy} onClick={() => void pdf()}>
          {busy === 'pdf' ? 'Making the PDF…' : 'PDF'}
        </button>
      </div>
      {can.why && <p className="muted small">{can.why}</p>}
      {note && (
        <p className="notice ok" role="status">
          {note}
        </p>
      )}
      {link && (
        <label className="f">
          Link to the invoice
          <input className="field" readOnly value={link} onFocus={(e) => e.target.select()} />
        </label>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
